import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShoppingCart } from 'lucide-react';
import { api } from '../../../api';
import { useCoinBalance } from '../../../contexts/CoinBalanceContext';
import useShopStore from '../../../store/shopStore';
import TransactionHistoryModal from '../coins/TransactionHistoryModal';

const EMOTIONS = [
  { value: 'happy', emoji: '😊', label: "I'm feeling happy" },
  { value: 'excited', emoji: '🤩', label: "I'm feeling excited" },
  { value: 'neutral', emoji: '😐', label: "I'm feeling okay" },
  { value: 'sad', emoji: '😢', label: "I'm feeling sad" },
  { value: 'very_sad', emoji: '😭', label: "I'm feeling very sad" },
];

// Look of a switched-off top bar button.
const OFF = 'disabled:opacity-50 disabled:cursor-not-allowed';

// Opens the shop's cart drawer (rendered by StudentShopLayout); the badge is the cart's item count.
function CartButton() {
  const { cartItemCount, toggleCart } = useShopStore();
  const itemCount = cartItemCount();

  return (
    <button
      onClick={toggleCart}
      className="relative p-2 text-gray-600 hover:bg-white rounded-xl transition-colors"
      aria-label="Shopping cart"
    >
      <ShoppingCart className="w-7 h-7" />
      {itemCount > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 bg-purple-600 text-white text-[11px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
          aria-label={`${itemCount} items in cart`}
        >
          {itemCount > 99 ? '99+' : itemCount}
        </span>
      )}
    </button>
  );
}

// `shopActive`: on the student's Shop pages - the Shop button shows as the current page and the cart button appears.
export default function LearningZoneTopBar({ shopActive = false }) {
  const navigate = useNavigate();
  const { balance } = useCoinBalance();
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [homeworkCount, setHomeworkCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showCoinHistory, setShowCoinHistory] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationsList, setNotificationsList] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const notificationRef = useRef(null);

  const fetchNotificationCount = () =>
    api
      .get('/api/notifications/unread-count')
      .then((res) => res.data?.success && setUnreadCount(res.data.data?.count || 0))
      .catch(() => {});

  useEffect(() => {
    const studentId = localStorage.getItem('userId');
    if (!studentId) return;

    api
      .get(`/api/v2/lms/student/${studentId}/homework/pending`)
      .then((res) => res.data?.success && setHomeworkCount(res.data.count || 0))
      .catch(() => {});

    fetchNotificationCount();
  }, []);

  // Notifications dropdown - same flow as the site-wide TitleBar's bell:
  // opening it marks everything read, then loads the latest 20.
  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const response = await api.get('/api/notifications', { params: { limit: 20, skip: 0 } });
      const data = response.data?.data || response.data?.notifications || [];
      setNotificationsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotificationsList([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleNotificationClick = async () => {
    const nextOpen = !showNotifications;
    setShowNotifications(nextOpen);
    if (!nextOpen) return;

    try {
      await api.put('/api/notifications/mark-all-read');
      setUnreadCount(0);
      setNotificationsList([]);
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
    await fetchNotifications();
    await fetchNotificationCount();
  };

  const handleNotificationItemClick = async (notification) => {
    try {
      await api.put(`/api/notifications/${notification._id}/read`);
      await fetchNotifications();
      await fetchNotificationCount();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }

    const actionUrl = notification?.metadata?.actionUrl;
    if (actionUrl) {
      setShowNotifications(false);
      navigate(actionUrl);
    }
  };

  const handleDeleteNotification = async (event, notificationId) => {
    event.stopPropagation();
    try {
      await api.delete(`/api/notifications/${notificationId}`);
      setNotificationsList((prev) => prev.filter((notification) => notification._id !== notificationId));
      await fetchNotificationCount();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEmotionClick = async (emotion) => {
    setSelectedEmotion(emotion);
    try {
      await api.post('/api/v1/mood-tracker', { mood: emotion, date: new Date().toISOString() });
      toast.success('Thanks for sharing how you feel!');
    } catch (error) {
      console.error('Failed to save mood:', error);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mb-6">
      <div className="flex items-center gap-1.5 bg-white rounded-2xl border border-gray-100 shadow-sm px-3 py-2">
        {EMOTIONS.map(({ value, emoji, label }) => (
          <button
            key={value}
            onClick={() => handleEmotionClick(value)}
            title={label}
            aria-label={label}
            className={`text-2xl w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
              selectedEmotion === value ? 'bg-blue-100 ring-2 ring-blue-400' : 'hover:bg-gray-100'
            }`}
          >
            {emoji}
          </button>
        ))}
      </div>

      {/* Chat with Amma, Homework and Help are switched off for students for now (2026-10-27): greyed out
          and not clickable. Their click handlers stay, so removing `disabled` brings them back. */}
      <button
        onClick={() => toast('Voice chat coming soon!', { icon: '🎤' })}
        disabled
        className={`flex items-center gap-2 px-5 py-3 bg-blue-600 text-white font-bold rounded-2xl shadow-sm enabled:hover:bg-blue-700 transition-colors ${OFF}`}
      >
        <span className="text-lg">🎙️</span> Chat with Amma
      </button>

      <button
        onClick={() => navigate('/student/homework')}
        disabled
        className={`relative flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-2xl shadow-sm enabled:hover:bg-gray-50 transition-colors ${OFF}`}
      >
        <span className="text-lg">📚</span> Homework
        {homeworkCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[11px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {homeworkCount > 9 ? '9+' : homeworkCount}
          </span>
        )}
      </button>

      <button
        onClick={() => toast('Help is on the way!', { icon: '❓' })}
        disabled
        className={`flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-2xl shadow-sm enabled:hover:bg-gray-50 transition-colors ${OFF}`}
      >
        <span className="text-lg">❓</span> Help
      </button>

      <div className="ml-auto flex items-center gap-3">
        <button
          onClick={() => setShowCoinHistory(true)}
          className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2 text-left hover:bg-amber-100 transition-colors"
          aria-label="View coin balance and transaction history"
        >
          <span className="text-2xl">🪙</span>
          <div className="leading-tight">
            <p className="font-extrabold text-amber-800 text-lg">
              {typeof balance === 'number' ? balance.toLocaleString() : '...'}
            </p>
            <p className="text-[11px] font-semibold text-amber-600 -mt-0.5">ISF Coins</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/shop')}
          aria-current={shopActive ? 'page' : undefined}
          className={`flex items-center gap-2 px-5 py-3 bg-orange-500 text-white font-bold rounded-2xl shadow-sm hover:bg-orange-600 transition-colors ${
            shopActive ? 'ring-4 ring-orange-200' : ''
          }`}
        >
          <span className="text-lg">🛒</span> Shop
        </button>

        {shopActive && <CartButton />}

        <div className="relative" ref={notificationRef}>
          <button
            onClick={handleNotificationClick}
            className="relative p-2 text-gray-600 hover:bg-white rounded-xl transition-colors"
            aria-label="Notifications"
            aria-expanded={showNotifications}
          >
            <span className="text-2xl">🔔</span>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[11px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 z-[70] w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-900">Notifications</h2>
                <button
                  onClick={() => setShowNotifications(false)}
                  className="text-gray-500 hover:text-gray-800 text-lg leading-none"
                  aria-label="Close notifications"
                >
                  x
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notificationsLoading ? (
                  <div className="px-4 py-6 text-sm text-gray-500 text-center">Loading notifications...</div>
                ) : notificationsList.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-gray-500 text-center">No notifications</div>
                ) : (
                  notificationsList.map((notification) => (
                    <div
                      key={notification._id}
                      className={`flex items-start gap-2 border-b border-gray-100 hover:bg-purple-50 transition-colors ${
                        !notification.isRead ? 'bg-purple-50/70' : 'bg-white'
                      }`}
                    >
                      <button
                        onClick={() => handleNotificationItemClick(notification)}
                        className="flex-1 text-left px-4 py-3 pr-1"
                      >
                        <div className="text-sm font-semibold text-gray-900">{notification.title}</div>
                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">{notification.message}</div>
                        <div className="text-[11px] text-gray-400 mt-2">
                          {notification.createdAt ? new Date(notification.createdAt).toLocaleString() : ''}
                        </div>
                      </button>
                      <button
                        onClick={(event) => handleDeleteNotification(event, notification._id)}
                        className="mt-2 mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-red-500 hover:bg-red-50 hover:text-red-700"
                        aria-label="Delete notification"
                        title="Delete notification"
                      >
                        x
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Portalled to <body> so the Wall of Fame's fixed z-50 "+" button,
          which comes later in the page, can't paint over the modal. */}
      {createPortal(
        <TransactionHistoryModal
          isOpen={showCoinHistory}
          onClose={() => setShowCoinHistory(false)}
          currentBalance={balance ?? 0}
        />,
        document.body
      )}
    </div>
  );
}
